import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  OnDestroy,
  signal,
} from "@angular/core";
import { FormBuilder, FormControl, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import {
  MultiFactorInfo,
  PhoneMultiFactorInfo,
  TotpMultiFactorGenerator,
} from "firebase/auth";
import { AuthService } from "../auth.service";
import { UsersService } from "../../users/users.service";

@Component({
    selector: "app-login-page",
    templateUrl: "./login-page.component.html",
    styleUrls: ["./login-page.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LoginPageComponent implements OnDestroy {
  public authService = inject(AuthService);
  private router = inject(Router);
  private userService = inject(UsersService);
  private formBuilder = inject(FormBuilder);

  // The dark admin gradient is painted by .login-container, which may fall
  // a little short of the full scrollable page height (exact app-toolbar
  // height varies by breakpoint). Rather than chase pixel-perfect vh math,
  // match the page background itself while this component is active so
  // there's never a mismatched-color gap below the fold.
  private static readonly BODY_CLASS = "admin-login-active";

  unknownNumberStr = $localize`Unknown Number`;
  loading = signal<boolean>(false);
  resendCooldown = signal<number>(0);
  private timerInterval: any;

  loginForm = this.formBuilder.group({
    email: new FormControl<string | null>("", [
      Validators.required,
      Validators.email,
    ]),
    password: new FormControl<string | null>("", [Validators.required]),
  });

  mfaForm = this.formBuilder.group({
    code: new FormControl<string>("", [
      Validators.required,
      Validators.minLength(6),
      Validators.maxLength(6),
    ]),
  });

  selectionForm = this.formBuilder.group({
    selectedHintIndex: new FormControl<number | null>(null, [
      Validators.required,
    ]),
  });

  constructor() {
    document.body.classList.add(LoginPageComponent.BODY_CLASS);

    effect(() => {
      if (this.authService.authStep() === "mfa") {
        this.startCooldown(90);
      }
    });
  }

  login() {
    this.loading.set(true);
    const email = this.loginForm.value.email;
    const password = this.loginForm.value.password;

    this.authService
      .login({ email: email!, password: password! })
      .then((status) => {
        if (status === "SUCCESS") {
          this.finalizeLoginFlow();
        } else if (status === "MFA_SETUP") {
          this.mfaForm.reset();
          this.loading.set(false);
        }
      })
      .catch((error) => {
        this.loading.set(false);
        if (
          error?.status !== "MFA_REQUIRED" &&
          error?.status !== "MFA_SELECTION_REQUIRED" &&
          error?.status !== "UNAUTHORIZED"
        ) {
          console.log("User error", error);
        }
      });
  }

  enrollTotpSetup() {
    if (this.mfaForm.invalid) return;
    this.loading.set(true);
    const code = this.mfaForm.value.code!;

    this.authService
      .verifyAndEnrollTotp(code)
      .then(() => this.finalizeLoginFlow())
      .catch(() => this.loading.set(false));
  }

  skipTotpSetup() {
    this.loading.set(true);
    this.authService.skipMfaSetup().then(() => this.finalizeLoginFlow());
  }

  cancelTotpSetup() {
    this.authService.logout();
    this.mfaForm.reset();
  }

  sendCodeToSelectedNumber() {
    this.loading.set(true);
    const index = this.selectionForm.value.selectedHintIndex;

    if (index !== null && index !== undefined) {
      const selectedHint = this.authService.phoneHints()[index];

      this.authService.selectedHint.set(selectedHint);
      this.authService
        .sendSmsCode(selectedHint)
        .then(() => {
          this.loading.set(false);
        })
        .catch(() => this.loading.set(false));
    } else {
      this.loading.set(false);
    }
  }

  getHintLabel(hint: MultiFactorInfo): string {
    if (hint.factorId === TotpMultiFactorGenerator.FACTOR_ID) {
      return hint.displayName || $localize`Authenticator App`;
    }
    if (hint.factorId === "phone") {
      return (
        (hint as PhoneMultiFactorInfo).phoneNumber || this.unknownNumberStr
      );
    }
    return $localize`Unknown method`;
  }

  verifyMfa() {
    this.loading.set(true);

    const code = this.mfaForm.value.code!;
    const selectedHint = this.authService.selectedHint();
    let verifyPromise: Promise<"SUCCESS" | "MFA_SETUP">;

    if (selectedHint?.factorId === TotpMultiFactorGenerator.FACTOR_ID) {
      verifyPromise = this.authService.verifyTotpCode(code);
    } else {
      verifyPromise = this.authService.verifyMfaCode(code);
    }

    verifyPromise
      .then((status) => {
        if (status === "SUCCESS") {
          this.finalizeLoginFlow();
        } else if (status === "MFA_SETUP") {
          this.mfaForm.reset();
          this.loading.set(false);
        }
      })
      .catch((err) => {
        this.loading.set(false);
        console.log("MFA Error", err);
      });
  }

  switchToSmsFallback() {
    this.loading.set(true);
    this.authService.switchToSmsFallback().then(() => {
      this.loading.set(false);
    });
  }

  resendCode() {
    this.loading.set(true);
    this.authService
      .resendSmsCode()
      .then(() => {
        this.loading.set(false);
        this.startCooldown(30);
      })
      .catch((err) => {
        this.loading.set(false);
        console.error("Resend error", err);
      });
  }

  cancelMfa() {
    this.authService.resetMfaState();
    this.mfaForm.reset();
  }

  private startCooldown(seconds: number) {
    this.resendCooldown.set(seconds);
    if (this.timerInterval) clearInterval(this.timerInterval);

    this.timerInterval = setInterval(() => {
      const current = this.resendCooldown();
      if (current <= 1) {
        clearInterval(this.timerInterval);
        this.resendCooldown.set(0);
      } else {
        this.resendCooldown.set(current - 1);
      }
    }, 1000);
  }

  private finalizeLoginFlow() {
    this.userService.checkTotpStatus();
    this.router.navigate(["/"]).then(() => {
      this.loading.set(false);
    });
  }

  ngOnDestroy(): void {
    this.loading.set(false);
    this.authService.resetMfaState();
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
    document.body.classList.remove(LoginPageComponent.BODY_CLASS);
  }
}
